import { authApiService } from './authService';
import { API } from './api';
import {
  DEFAULT_FALLBACK_AMOUNTS,
  toCheckoutAmount,
} from '../utils/pricingDisplay';

const unwrap = (response) => {
  const payload = response?.data ?? response;
  if (payload?.body?.data) return payload.body.data;
  if (payload?.data) return payload.data;
  return payload;
};

export const pricingService = {
  getPublicPlans: async () => {
    const response = await API.get('/pricing');
    return unwrap(response);
  },

  getAdminPlans: async () => {
    const response = await authApiService.get('/admin/pricing');
    return unwrap(response);
  },

  createPlan: async (planData) => {
    const response = await authApiService.post('/admin/pricing', planData);
    return unwrap(response);
  },

  updatePlan: async (planId, updates) => {
    const response = await authApiService.put(`/admin/pricing/${planId}`, updates);
    return unwrap(response);
  },

  getPlanMeta: (plansByKey, planKey, billingPeriod) =>
    plansByKey?.[planKey]?.[billingPeriod] ?? null,

  getDescription: (plansByKey, planKey, billingPeriod) =>
    plansByKey?.[planKey]?.[billingPeriod]?.description ?? '',

  getAmountForPlan: (plansByKey, planKey, billingPeriod, seatCount = 1) => {
    const plan = plansByKey?.[planKey]?.[billingPeriod];
    if (!plan) return null;
    const amount = parseFloat(plan.amount);
    if (Number.isNaN(amount)) return null;
    if (plan.perSeat) {
      return parseFloat((amount * Math.max(1, seatCount)).toFixed(2));
    }
    return amount;
  },

  getAmountWithFallback: (plansByKey, planKey, billingPeriod, seatCount = 1) => {
    const fromApi = pricingService.getAmountForPlan(
      plansByKey,
      planKey,
      billingPeriod,
      seatCount
    );
    if (fromApi != null) return fromApi;

    const fallback = DEFAULT_FALLBACK_AMOUNTS[planKey]?.[billingPeriod];
    if (fallback == null) return null;

    return fallback;
  },

  /** Total charge for payment (annual = per-month rate × 12). */
  getCheckoutAmountForPlan: (plansByKey, planKey, billingPeriod, seatCount = 1) => {
    const perMonth = pricingService.getAmountForPlan(
      plansByKey,
      planKey,
      billingPeriod,
      seatCount
    );
    return toCheckoutAmount(perMonth, billingPeriod);
  },

  getCheckoutAmountWithFallback: (plansByKey, planKey, billingPeriod, seatCount = 1) => {
    const perMonth = pricingService.getAmountWithFallback(
      plansByKey,
      planKey,
      billingPeriod,
      seatCount
    );
    return toCheckoutAmount(perMonth, billingPeriod);
  },
};
